import React, { useEffect, useRef, useState } from 'react'
import { useField } from 'formik'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import {
  institutionLogoMap,
  practiceSoftwareLogoMap,
} from '@/src/constants/logoConstants'
import { US_STATES_WITH_NAMES } from '@/constants/locationConstants'
import {
  Checkbox,
  Form,
  Icon,
  Image,
  Input,
  Multiselect,
  SearchableSelect,
  Select,
  Slider,
  Text,
  Toggle,
} from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'

import {
  formatThousands,
  INCOME_BAND_SLIDER_MAX,
  incomeAmountFieldName,
  isNotApplicableAnswer,
  isSentinelAnswer,
  isUnknownAnswer,
  NOT_APPLICABLE_ANSWER,
  stateName,
  UNKNOWN_ANSWER,
} from '../lib/constants'
import { optionLabelFor } from '../questions/registry'
import type { QuestionOption } from '../questions/types'

/** Compact pills (plain yes/no, no sublabel) always sit directly next to each
 *  other — a grid `columns` layout would stretch each option into its own
 *  wide column, stranding the small pill at that column's left edge and
 *  reading as a huge gap between them. */
const gridCls = (columns?: 2 | 3, compact?: boolean): string => {
  if (compact) return 'flex flex-row flex-wrap gap-3'
  return columns === 3
    ? 'grid grid-cols-1 gap-3 sm:grid-cols-3'
    : columns === 2
      ? 'grid grid-cols-1 gap-3 sm:grid-cols-2'
      : 'flex flex-col flex-wrap gap-3 sm:flex-row'
}

/** A handful of DS tints, cycled by option index — until real per-option brand
 *  logos exist, each initial-avatar option at least gets a distinct color. */
const INITIAL_AVATAR_PALETTE = [
  { bg: 'bg-blue-25', text: 'text-blue-200' },
  { bg: 'bg-purple-25', text: 'text-purple-200' },
  { bg: 'bg-pink-25', text: 'text-pink-200' },
  { bg: 'bg-green-25', text: 'text-green-100' },
  { bg: 'bg-orange-25', text: 'text-orange-200' },
]

/** Every option label the intake can show a real brand logo for: banks, and
 *  the practice-management systems. Keyed case-insensitively so a label only
 *  has to match by name, not by exact casing. */
const logoByNormalizedName = new Map(
  Object.entries({ ...institutionLogoMap, ...practiceSoftwareLogoMap }).map(
    ([name, src]) => [name.toLowerCase(), src]
  )
)
export const logoFor = (label: string): string | undefined =>
  logoByNormalizedName.get(label.trim().toLowerCase())

const OptionBadge: React.FC<{
  icon?: IconDefinition
  emoji?: string
  badge?: 'logo' | 'initial'
  label: string
  index: number
}> = ({ icon, emoji, badge, label, index }) => {
  // Bare, at roughly the icon circle's optical weight. No tinted disc behind
  // it: emoji bring their own color, and sitting one on a grey circle reads
  // as a placeholder that failed to load rather than a deliberate glyph.
  if (emoji) {
    return (
      <span aria-hidden="true" className="shrink-0 text-xl leading-none">
        {emoji}
      </span>
    )
  }
  if (badge === 'logo') {
    // Rendered here rather than through `AccountInstitutionLogo`: that
    // component exists to read a linked `PlaidItem`'s logo, and the intake
    // asks these questions before any Plaid link exists. It also now covers
    // EHRs, which are not institutions at all.
    const src = logoFor(label)
    // "Other", "None yet" and "Local bank or credit union" are categories
    // rather than brands. They get no badge at all: a generic placeholder
    // beside real logos reads as an image that failed to load.
    if (!src) return null
    return (
      <div className="size-9 shrink-0 overflow-hidden rounded-lg">
        <Image src={src} alt="" width={36} height={36} />
      </div>
    )
  }
  if (badge === 'initial') {
    const { bg, text } =
      INITIAL_AVATAR_PALETTE[index % INITIAL_AVATAR_PALETTE.length]
    return (
      <span
        className={mtc(
          'flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-medium',
          bg,
          text
        )}
      >
        {label.charAt(0).toUpperCase()}
      </span>
    )
  }
  if (icon) {
    return (
      <Icon
        icon={icon}
        size="sm"
        color="text-neutral-500"
        backgroundColor="transparent"
        backgroundClassName="bg-neutral-100"
        backgroundStyle="circle"
      />
    )
  }
  return null
}

interface OptionCardBaseProps {
  label: string
  sublabel?: string
  /** Placeholder logo badge — a generic icon until real per-option brand logos exist. */
  icon?: IconDefinition
  /** Emoji shown in place of `icon`. Decorative: the option's own label
   *  already names it, so it's hidden from assistive tech. */
  emoji?: string
  badge?: 'logo' | 'initial'
  /** Position within the option list — picks this card's initial-avatar color. */
  index?: number
  /** Narrow (72px) pill instead of the full descriptive card — for plain
   *  yes/no options with no sublabel. */
  compact?: boolean
}

/** Delegates selection to `Form.Toggle`'s own Formik-array logic. */
interface FormikOptionCardProps extends OptionCardBaseProps {
  name: string
  value: string
  multiSelect?: boolean
  checked?: never
  onToggle?: never
}

/** Renders a plain `Toggle` the caller drives, for custom selection rules
 *  (e.g. mutually exclusive options) or a screen with no Formik form at all
 *  (e.g. `CloserScreen`). */
interface ControlledOptionCardProps extends OptionCardBaseProps {
  checked: boolean
  onToggle: () => void
  name?: never
  value?: never
  multiSelect?: never
}

/** The two variants are mutually exclusive — a card is either Formik-bound
 *  or caller-controlled, never a mix, and the type says so rather than the
 *  component checking at render time. */
export type OptionCardProps = FormikOptionCardProps | ControlledOptionCardProps

/** DS `Form.Toggle` (or, when `checked`/`onToggle` are passed, the plain
 *  controlled `Toggle`), card variant — matches the same component
 *  Storybook documents for grouped card-style options. Exported for callers
 *  outside a Formik form (e.g. `CloserScreen`'s concern tiles) that still
 *  want the same card look via the controlled variant. */
export const OptionCard: React.FC<OptionCardProps> = (props) => {
  const { label, sublabel, icon, emoji, badge, index = 0, compact } = props
  const labelBlock = (
    <span
      className={mtc(
        'flex flex-col',
        compact ? 'items-center text-center' : 'text-left'
      )}
    >
      <span className="text-sm font-medium text-neutral-700">{label}</span>
      {sublabel && <span className="text-xs text-neutral-500">{sublabel}</span>}
    </span>
  )
  const children =
    icon || emoji || badge ? (
      <span className="flex items-center gap-2">
        <OptionBadge
          icon={icon}
          emoji={emoji}
          badge={badge}
          label={label}
          index={index}
        />
        {labelBlock}
      </span>
    ) : (
      labelBlock
    )
  const className = mtc(
    // The DS card variant is `flex-col`, so its `items-start` is the *left*
    // alignment and must stay. `justify-center` is the vertical one: cards
    // stretch to the tallest in the row, so an option with no badge (or no
    // sublabel) otherwise hangs its label off the top while its logo-bearing
    // neighbours look centred.
    'w-full justify-center sm:w-auto',
    compact && 'items-center text-center sm:min-w-[80px]'
  )

  if (props.onToggle) {
    return (
      <Toggle
        variant="card"
        size="card"
        className={className}
        checked={props.checked}
        onCheckedChange={props.onToggle}
      >
        {children}
      </Toggle>
    )
  }

  return (
    <Form.Toggle
      name={props.name}
      value={props.value}
      multiSelect={props.multiSelect}
      variant="card"
      size="card"
      className={className}
    >
      {children}
    </Form.Toggle>
  )
}

export const SingleSelect: React.FC<{
  name: string
  options: QuestionOption[]
  columns?: 2 | 3
  icon?: IconDefinition
  badge?: 'logo' | 'initial'
  compact?: boolean
}> = ({ name, options, columns, icon, badge, compact }) => (
  <div className={gridCls(columns, compact)}>
    {options.map((o, index) => (
      <OptionCard
        key={o.value}
        name={name}
        value={o.value}
        label={o.label}
        sublabel={o.sublabel}
        badge={badge}
        index={index}
        icon={icon}
        compact={compact}
      />
    ))}
  </div>
)

/** Selecting an `exclusive` option (e.g. "None of these") clears every other
 *  selection; selecting any other option clears the exclusive one back out. */
export const MultiSelect: React.FC<{
  name: string
  options: QuestionOption[]
  columns?: 2 | 3
}> = ({ name, options, columns }) => {
  const [field, , helpers] = useField<string[]>(name)
  const value = field.value ?? []
  const toggle = (option: QuestionOption): void => {
    if (value.includes(option.value)) {
      helpers.setValue(value.filter((v) => v !== option.value))
      return
    }
    if (option.exclusive) {
      helpers.setValue([option.value])
      return
    }
    const exclusiveValues = options
      .filter((o) => o.exclusive)
      .map((o) => o.value)
    helpers.setValue([
      ...value.filter((v) => !exclusiveValues.includes(v)),
      option.value,
    ])
  }

  return (
    <div className={gridCls(columns)}>
      {options.map((o, index) => (
        <OptionCard
          key={o.value}
          label={o.label}
          sublabel={o.sublabel}
          index={index}
          checked={value.includes(o.value)}
          onToggle={() => toggle(o)}
        />
      ))}
    </div>
  )
}

/* The code is searchable but never displayed: "north carolina" does not
 * contain "nc", so without it typing a code found nothing for most states. */
const STATE_MULTISELECT_OPTIONS = US_STATES_WITH_NAMES.map((s) => ({
  value: s.code,
  text: s.name,
  searchText: s.code,
}))
const STATE_SEARCHABLE_OPTIONS = US_STATES_WITH_NAMES.map((s) => ({
  value: s.code,
  label: s.name,
  searchText: s.code,
}))

const MultiStateSelect: React.FC<{
  name: string
  prepopulateFrom?: string[]
}> = ({ name, prepopulateFrom }) => {
  const [field, , helpers] = useField<string[]>(name)
  // Mirrors `prepopulateFrom` until the user edits this field themselves, at
  // which point it stops following and the divergence sticks.
  //
  // This deliberately is not a one-shot seed. This question is revealed before
  // the one it copies has been answered, so a one-shot seed fires on the first
  // state picked and then ignores every later one, leaving "where do you
  // provide care" stuck on a single state the user has since changed.
  const userEdited = useRef(false)
  // A value already present at mount came from a previous session, so it is
  // the user's own answer and must not be overwritten by the mirror.
  const arrivedWithValue = useRef((field.value?.length ?? 0) > 0)
  useEffect(() => {
    if (userEdited.current || arrivedWithValue.current) return
    const next = prepopulateFrom ?? []
    const current = field.value ?? []
    // Writing an identical value would still re-render, and this effect keys
    // off a prop the caller rebuilds, so bail rather than trust that the
    // identity upstream is stable.
    if (
      next.length === current.length &&
      next.every((state, i) => state === current[i])
    ) {
      return
    }
    helpers.setValue(next)
    // Only the source field should drive this. `field.value`/`helpers` are
    // read fresh each run via the closure, and the refs above decide whether
    // the mirror still applies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepopulateFrom])

  return (
    // The DS `Multiselect` takes no `className`, so this reaches its trigger
    // through the stable class the component puts there. Its own `py-2`
    // leaves 24px of content box, which neither the 30px search input nor a
    // 32px chip fits, so the control sits taller than a standard field when
    // empty, and taller again once a chip lands. `py-1` makes one row a
    // consistent 40px, matching every other control; it still grows a row at
    // a time as chips wrap.
    <div className="[&_.multi-select-trigger]:py-1">
      <Multiselect
        options={STATE_MULTISELECT_OPTIONS}
        value={field.value ?? []}
        onChange={(value) => {
          userEdited.current = true
          helpers.setValue(value)
        }}
        searchable
        placeholder="Search states…"
      />
    </div>
  )
}

const SingleStateSelect: React.FC<{ name: string }> = ({ name }) => {
  const [field, , helpers] = useField<string | undefined>(name)
  return (
    <div className="max-w-xs">
      <SearchableSelect
        options={STATE_SEARCHABLE_OPTIONS}
        value={field.value}
        onChange={(value) => helpers.setValue(value)}
        placeholder="Search states…"
      />
    </div>
  )
}

export const StateSelect: React.FC<{
  name: string
  multi: boolean
  /** `multi` only: seed this field's starting value from another question's
   *  answer, once, if this one is still empty. */
  prepopulateFrom?: string[]
}> = ({ name, multi, prepopulateFrom }) =>
  multi ? (
    <MultiStateSelect name={name} prepopulateFrom={prepopulateFrom} />
  ) : (
    <SingleStateSelect name={name} />
  )

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** Years selectable for a given bound — a decade-plus window covers
 *  realistic renewal/formation/launch dates without an unbounded list. */
const yearOptions = (bound: 'future' | 'past' | undefined): number[] => {
  const currentYear = new Date().getFullYear()
  return bound === 'future'
    ? Array.from({ length: 11 }, (_, i) => currentYear + i)
    : Array.from({ length: 31 }, (_, i) => currentYear - i)
}

/** Months selectable for a year already narrowed by `bound`. `yearOptions`
 *  bounds only the year, so the boundary year is the one that can still
 *  compose an out-of-bounds date. `future` has the mirror-image gap and is
 *  deliberately left open. */
const monthOptions = (
  bound: 'future' | 'past' | undefined,
  year: number | undefined
): { value: number; text: string }[] => {
  const now = new Date()
  const boundedByMonth = bound === 'past' && year === now.getFullYear()
  return MONTH_NAMES.map((m, i) => ({ value: i + 1, text: m })).filter(
    ({ value }) => !boundedByMonth || value <= now.getMonth() + 1
  )
}

const parseYearMonth = (
  value: string | null | undefined
): { year: number | undefined; month: number | undefined } => {
  // A sentinel is a non-empty string, so it survives the falsy guard and would
  // parse to NaN and render as a selected year.
  if (!value || isSentinelAnswer(value)) {
    return { year: undefined, month: undefined }
  }
  const [y, m] = value.split('-').map(Number)
  return { year: y, month: m }
}

/**
 * A year with no month resolved to a full date. A deadline (`future`) takes the
 * end of the year: reading a year-only expiry as 1 January invents a lapse the
 * user never reported. An origin (`past`) takes 1 January, the conservative end.
 */
const yearMonthValue = (
  year: number,
  month: number | undefined,
  bound: 'future' | 'past' | undefined
): string => {
  if (month !== undefined) return `${year}-${pad2(month)}-01`
  return bound === 'future' ? `${year}-12-31` : `${year}-01-01`
}

type DateSentinel = typeof UNKNOWN_ANSWER | typeof NOT_APPLICABLE_ANSWER

const sentinelOf = (value: unknown): DateSentinel | undefined => {
  if (isUnknownAnswer(value)) return UNKNOWN_ANSWER
  if (isNotApplicableAnswer(value)) return NOT_APPLICABLE_ANSWER
  return undefined
}

/**
 * Year required, month optional. The selects are driven by local state seeded
 * once on mount, not by re-parsing the stored value, so a year-only pick never
 * renders as though January was chosen.
 */
export const YearMonthDateField: React.FC<{
  name: string
  bound?: 'future' | 'past'
}> = ({ name, bound }) => {
  const [field, , helpers] = useField<string | null | undefined>(name)
  const [local, setLocal] = useState(() => parseYearMonth(field.value))
  const [sentinel, setSentinel] = useState(() => sentinelOf(field.value))
  // Local state is seeded once on mount, so an external clear (a gating answer
  // changing elsewhere) would otherwise leave a sentinel ticked over a value
  // that is gone. Scoped to an emptied field on purpose: reacting to a date
  // would reintroduce the write-time default this local state exists to hide.
  useEffect(() => {
    if (field.value === null || field.value === undefined) {
      setSentinel(undefined)
      setLocal({ year: undefined, month: undefined })
    }
  }, [field.value])
  const years = yearOptions(bound)
  const months = monthOptions(bound, local.year)

  const commit = (
    nextYear: number | undefined,
    nextMonth: number | undefined
  ): void => {
    setLocal({ year: nextYear, month: nextMonth })
    setSentinel(undefined)
    if (nextYear === undefined) {
      helpers.setValue(null)
      return
    }
    helpers.setValue(yearMonthValue(nextYear, nextMonth, bound))
  }

  const pickSentinel = (next: DateSentinel | undefined): void => {
    setSentinel(next)
    if (next) {
      setLocal({ year: undefined, month: undefined })
      helpers.setValue(next)
      return
    }
    // `undefined`, not a sentinel: `everyStateAnswered` counts any other value
    // as answered, so unticking has to clear the row.
    helpers.setValue(undefined)
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-full sm:w-24">
        <Select
          options={years.map((y) => ({ value: y, text: String(y) }))}
          value={local.year}
          disabled={sentinel !== undefined}
          // Always reset month on a year change, rather than carrying
          // `local.month` forward — a reopened field's local state may have
          // just been re-seeded from a stored value whose month was only
          // ever a write-time default (see the note above), and carrying
          // that over would make it look like this year's month was
          // already chosen when it never was.
          onValueChange={(y) => commit(y, undefined)}
          placeholder="Year"
        />
      </div>
      <div className="w-full sm:w-40">
        <Select
          options={months}
          value={local.month}
          disabled={sentinel !== undefined}
          onValueChange={(m) => commit(local.year, m)}
          placeholder="Month (optional)"
        />
      </div>
      <Checkbox
        checked={sentinel === UNKNOWN_ANSWER}
        // The atom derives its accessible name and its id from `label` only
        // when that is a string, so a node label needs this too.
        ariaLabel="Skip, I Don't Know"
        label={
          <Text variant={TypographyStyles.BODY_XS}>
            Skip, I Don&apos;t Know
          </Text>
        }
        onCheckedChange={(next) =>
          pickSentinel(next ? UNKNOWN_ANSWER : undefined)
        }
      />
    </div>
  )
}

/**
 * Year-first date for something that has already happened: a previous year
 * is precise enough on its own (no month shown at all), but the current year
 * alone is ambiguous — most of this year has already happened — so picking
 * the current year reveals a month select scoped to Jan–now (a future month
 * this year can't be a past date). Past-only by construction, so unlike
 * `YearMonthDateField` it takes no `bound`. Local state for the same reason
 * as that field — the month select shouldn't read back "January" the moment
 * a current-year value round-trips through the default-day-1 write.
 *
 * Where the two fields part company: here the month is *required* once the
 * current year is picked, so nothing is stored until it lands. Elsewhere a
 * year-only answer is a fine approximation, but this one becomes
 * `approximatePracticeStartDate`, the date catch-up bookkeeping is counted
 * and priced from, and defaulting it to January would quietly bill from a
 * date the practitioner never chose. Only `practice_launched_at` uses this
 * field, so the stricter rule is scoped to it by construction.
 */
export const RecentPastDateField: React.FC<{ name: string }> = ({ name }) => {
  const [field, , helpers] = useField<string | undefined>(name)
  const [local, setLocal] = useState(() => parseYearMonth(field.value))
  const now = new Date()
  const currentYear = now.getFullYear()
  const isCurrentYear = local.year === currentYear
  const years = yearOptions('past')
  const monthOptions = MONTH_NAMES.slice(
    0,
    isCurrentYear ? now.getMonth() + 1 : 12
  )

  const commit = (nextYear: number, nextMonth: number | undefined): void => {
    setLocal({ year: nextYear, month: nextMonth })
    // The year still shows (local state drives the selects), it just isn't an
    // answer yet, so the question stays open and Continue keeps blocking until
    // the month is chosen.
    if (nextYear === currentYear && nextMonth === undefined) {
      helpers.setValue(undefined)
      return
    }
    helpers.setValue(`${nextYear}-${pad2(nextMonth ?? 1)}-01`)
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-full sm:w-24">
        <Select
          options={years.map((y) => ({ value: y, text: String(y) }))}
          value={local.year}
          // Always reset month on a year change — same reasoning as
          // `YearMonthDateField` above (a reopened field's local month may
          // just be a re-seeded write-time default, not a real choice).
          onValueChange={(y) => commit(y, undefined)}
          placeholder="Year"
        />
      </div>
      {isCurrentYear && (
        <div className="w-full sm:w-40">
          <Select
            options={monthOptions.map((m, i) => ({ value: i + 1, text: m }))}
            value={local.month}
            onValueChange={(m) => commit(currentYear, m)}
            placeholder="Month"
          />
        </div>
      )}
    </div>
  )
}

export const PerStateDateField: React.FC<{
  name: string
  states: string[]
  bound?: 'future' | 'past'
}> = ({ name, states, bound }) => {
  if (states.length === 0) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Select your licensed states first.
      </Text>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      {/* Stacked below `sm`: a fixed 128px label column beside the controls leaves them
          about 120px on a phone, so the state name gets a third of the row and the date
          fields are squeezed into what is left. Above it, the label column keeps the rows
          aligned with each other, which is what makes a multi-state list scannable. */}
      {states.map((state) => (
        <div
          key={state}
          className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
        >
          <span className="text-sm text-neutral-700 sm:w-32 sm:shrink-0">
            {stateName(state)}
          </span>
          <YearMonthDateField name={`${name}.${state}`} bound={bound} />
        </div>
      ))}
    </div>
  )
}

export const PerStateSelect: React.FC<{
  name: string
  states: string[]
  options: QuestionOption[]
  /** Per-state option overrides (state code → valid options); falls back to `options`. */
  optionsByState?: Record<string, QuestionOption[]>
}> = ({ name, states, options, optionsByState }) => {
  if (states.length === 0) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Select your licensed states first.
      </Text>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      {states.map((state) => {
        const stateOptions = optionsByState?.[state] ?? options
        return (
          // Stacked below `sm`, same reasoning as `PerStateDateField` above.
          <div
            key={state}
            className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
          >
            <span className="text-sm text-neutral-700 sm:w-32 sm:shrink-0">
              {stateName(state)}
            </span>
            {/* Select's trigger hardcodes w-full and never receives a
                forwarded className, so the only way to cap its width
                (matching the date input above) is a wrapping container. */}
            {/* `flex-1` only from `sm`, where the row is horizontal: in the stacked
                column below it, growing along the main axis would stretch the
                select's height instead of its width. */}
            <div className="w-full sm:max-w-xs sm:flex-1">
              <Form.Select
                name={`${name}.${state}`}
                options={stateOptions.map((o) => ({
                  value: o.value,
                  text: o.label,
                }))}
                placeholder="Select…"
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const NumberField: React.FC<{
  name: string
  unit?: string
  /** Renders a slider instead of a plain numeric input — for a rough count
   *  the user is estimating anyway, dragging reads faster than typing. */
  slider?: { min: number; max: number; step?: number }
}> = ({ name, unit, slider }) => {
  // Form.Input's number handling doesn't write back to Formik state, and its
  // text branch won't take an external value/onChange — bind this one field
  // manually so the stored value stays a number, matching what the backend expects.
  const [field, , helpers] = useField<number | undefined>(name)

  if (slider) {
    // Opens at the floor, and the floor is a real answer: zero clients a week
    // is the true answer for someone still setting up, so the readout states it
    // plainly rather than greying it out or asking to be dragged. The stored
    // answer still lags the display until the slider is touched, which is what
    // keeps an untouched question from rolling itself up as done.
    const touched = field.value !== undefined
    const value = field.value ?? slider.min
    // Radix only fires `onValueChange` when the value actually changes, so
    // every gesture that means "the position it already rests on" (clicking
    // the track under the thumb, ArrowLeft at the floor, pressing the thumb
    // without moving it) produces no event, and the floor becomes unanswerable
    // for exactly the people whose answer it is.
    const commitIfUntouched = (): void => {
      if (!touched) helpers.setValue(value)
    }
    return (
      <div className="flex max-w-sm flex-col gap-3">
        <Text variant={TypographyStyles.LABEL_MD}>
          {value}
          {unit && ` ${unit}`}
        </Text>
        {/* `py-3` grows the pressable root to ~40px without moving the track:
            the bar is only 6px tall, so without it the user has to hit a
            hairline. Radix maps a press anywhere on the root to the nearest
            value, so the padding is real target area, not decoration. */}
        <Slider
          className="py-3"
          value={[value]}
          onValueChange={([v]) => helpers.setValue(v)}
          onPointerUp={commitIfUntouched}
          onKeyUp={commitIfUntouched}
          min={slider.min}
          max={slider.max}
          step={slider.step ?? 1}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-28">
        <Input
          type="number"
          value={field.value ?? ''}
          onChange={(e) =>
            helpers.setValue(
              e.target.value === '' ? undefined : Number(e.target.value)
            )
          }
        />
      </div>
      {unit && (
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          {unit}
        </Text>
      )}
    </div>
  )
}

const INCOME_BAND_SLIDER_MIN = 0
const INCOME_BAND_SLIDER_STEP = 10_000

/** Dollar-amount breakpoints backing the `expected_income_band` enum the
 *  backend validates (`u20` / `20-40` / `40-80` / `80-150` / `150+`) — kept in
 *  sync with that contract rather than storing the raw slider amount, since the
 *  backend only accepts one of those band strings. */
export const bandForAmount = (amount: number): string => {
  if (amount < 20_000) return 'u20'
  if (amount < 40_000) return '20-40'
  if (amount < 80_000) return '40-80'
  if (amount < 150_000) return '80-150'
  return '150+'
}

/** A representative amount within each band — used only to seed the
 *  slider's starting position when there's no in-session drag position yet
 *  (e.g. an answer loaded from a prior session). The exact amount was never
 *  captured, only the band. */
/** The option label for a stored band, for a readout with no exact figure. */
const bandLabel = (band: string): string =>
  optionLabelFor('expected_income_band', band)

const REPRESENTATIVE_AMOUNT_FOR_BAND: Record<string, number> = {
  u20: 10_000,
  '20-40': 30_000,
  '40-80': 60_000,
  '80-150': 110_000,
  '150+': 180_000,
  // Every value round-trips: `bandForAmount(amount) === band`. The pre-split
  // 'u40' is deliberately absent — it spans u20 and 20-40, so no single position
  // round-trips to it. Its thumb parks at the floor and the readout names the
  // stored band, which is read from the answer rather than from the thumb.
}

/** Opening position for a slider with no answer behind it yet: the floor,
 *  which is a real answer here. A practice that hasn't started earning belongs
 *  at the bottom of the scale, not parked on a guess it has to correct. */
const INCOME_BAND_SLIDER_DEFAULT_POSITION = INCOME_BAND_SLIDER_MIN

export const IncomeBandSliderField: React.FC<{
  name: string
}> = ({ name }) => {
  const [field, , helpers] = useField<string | undefined>(name)
  // The exact amount lives in its own Formik field rather than local state, so
  // it survives this control unmounting when the question collapses — that's
  // what lets the collapsed summary show the number back instead of the band.
  const [amountField, , amountHelpers] = useField<number | undefined>(
    incomeAmountFieldName(name)
  )
  const touched = field.value !== undefined
  const amount =
    amountField.value ??
    (field.value === undefined
      ? undefined
      : REPRESENTATIVE_AMOUNT_FOR_BAND[field.value]) ??
    INCOME_BAND_SLIDER_DEFAULT_POSITION
  // Same resting-position problem as `NumberField`'s slider: the gestures
  // meaning "where it already sits" change nothing, so Radix stays silent and
  // the question can't be satisfied. Commit the amount as well as the band, so
  // an accepted default reads back as the figure rather than the band label.
  const commitIfUntouched = (): void => {
    if (touched) return
    amountHelpers.setValue(amount)
    helpers.setValue(bandForAmount(amount))
  }
  return (
    <div className="flex max-w-sm flex-col gap-3">
      {/* Shows the band rather than a figure only when we don't hold one: a
          returning session has just the band stored, and the representative
          amount behind the thumb is not a number the user ever chose. */}
      <Text variant={TypographyStyles.LABEL_MD}>
        {touched && amountField.value === undefined
          ? bandLabel(field.value as string)
          : formatThousands(amount)}
      </Text>
      {/* Same enlarged target as `NumberField`'s slider. */}
      <Slider
        className="py-3"
        value={[amount]}
        onPointerUp={commitIfUntouched}
        onKeyUp={commitIfUntouched}
        onValueChange={([v]) => {
          amountHelpers.setValue(v)
          helpers.setValue(bandForAmount(v))
        }}
        min={INCOME_BAND_SLIDER_MIN}
        max={INCOME_BAND_SLIDER_MAX}
        step={INCOME_BAND_SLIDER_STEP}
      />
    </div>
  )
}

export const TextField: React.FC<{
  name: string
  type?: 'text' | 'tel'
  placeholder?: string
}> = ({ name, type = 'text', placeholder }) => (
  <div className="max-w-xs">
    <Form.Input name={name} type={type} placeholder={placeholder} />
  </div>
)
